import { Component, OnInit, ViewChild, inject, DOCUMENT, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, map, timer } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { SignaturePad } from '@viablelogic/ngx-signature-pad';

import { EmployeeInfoService } from '@core/services/employee-info.service';
import { PortalSettingsService } from '@core/services/portal-settings.service';
import { UtilityService } from '@core/services/utility.service';
import { ChecklistService } from '@pages/checklist/services/checklist.service';
import { EmployeeApiService } from '@pages/employee/services/employee.api.service';
import { SmService } from '@pages/sm/services/sm.service';
import { ClockInOutService } from '@core/services/clock-in-out.service';
import { LineType } from '@shared/model/line-type.model';
import { SimpleLookupResult } from '@typeDefs/general/general';
import { MechanicFormData } from '@shared/model/mechanic-type-data.model';
import { JobFormData } from '@shared/model/job-type-data.model';
import { ClockAction_Request } from '@typeDefs/employee/requests/employee';
import { SMGetWorkOrderScopes_Result, SMGetWorkOrderTripsByEmployee_Result } from '@typeDefs/field-service/responses/field-service';

@Component({
  selector: 'app-clock-in-out',
  templateUrl: './clock-in-out.component.html',
  styleUrls: ['./clock-in-out.component.css'],
  standalone: false,
})
export class ClockInOutComponent implements OnInit {
  private _signaturePad: SignaturePad | undefined;

  @ViewChild('signaturePad') set signaturePad(pad: SignaturePad | undefined) {
    this._signaturePad = pad;
    if (pad) {
      requestAnimationFrame(() => pad.resizeCanvas());
    }
  }

  get signaturePad(): SignaturePad | undefined {
    return this._signaturePad;
  }

  private readonly window = inject(DOCUMENT).defaultView!;
  private readonly portalSettings = inject(PortalSettingsService).getPortalSettings();
  private readonly employeeInfoService = inject(EmployeeInfoService);
  private readonly employeeApiService = inject(EmployeeApiService);
  private readonly checklistService = inject(ChecklistService);
  private readonly route = inject(ActivatedRoute);
  private readonly utilityService = inject(UtilityService);
  private readonly smService = inject(SmService);
  private readonly clockinoutService = inject(ClockInOutService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly mealBreakSettings: { prco: number; prgroup: number; minutes: number }[] = this.parseMealBreakSettings();

  readonly employeeInfo = this.employeeInfoService.getEmployeeInfo();
  readonly customMsg = this.portalSettings.CustomVerbiageClockOutSign.ConfigValue;
  readonly requireSign = this.portalSettings.RequireSignClockOut.ConfigValue == '1';
  readonly isUserFSTech = this.employeeInfo.IsActiveTechnician;
  readonly types = signal<LineType[]>(this.clockinoutService.getEnabledLineTypes(this.isUserFSTech));
  readonly isJobEnabled = computed(() => this.types().some(type => type.Value === 'J'));
  readonly isServiceEnabled = computed(() => this.types().some(type => type.Value === 'S'));
  readonly isMechanicTypeEnabled = computed(() => this.types().some(type => type.Value === 'M'));
  readonly kioskLogin = this.route.snapshot.queryParams?.kioskLogin;
  readonly mealBreakOptions: SimpleLookupResult[] = [
    { ComboType: null, DatabaseValue: 'N', DisplayValue: 'No' },
    { ComboType: null, DatabaseValue: 'Y', DisplayValue: 'Yes' },
  ] as const;

  geolocationEnabled = false;
  requestingLocation = false;
  position: GeolocationPosition | null = null;
  clockActionPerformed = false;
  performingClockAction = false;
  formData: any = {};
  loadingClockStatus = true;
  clockStatus: boolean | null = null;
  alert: { type: string; msg: string } = null;
  clockMoment: moment.Moment;
  lastClockDisplay: string;
  signatureData: any = null;
  clockActionSuccess: boolean;
  message: string;
  rawImgData: string | null = null;
  submitSignImg: string | null = null;
  hideClick: boolean;
  showClickMsg: boolean;
  redBorder = false;
  disableSubmitSign: boolean;
  workOrderList: SMGetWorkOrderTripsByEmployee_Result[] = [];
  scopeList: SMGetWorkOrderScopes_Result[] = [];
  payTypeList: SimpleLookupResult[] = [];
  lastClockinType: string;
  signaturePadOptions = {
    canvasWidth: 550,
    canvasHeight: 150,
  };

  mealBreak = false;
  hadMealBreakToday = false;
  hasWarning = false;

  ngOnInit(): void {
    this.clockinoutService.resetJobFormData(this.formData);
    this.formData.selectedType = null;
    this.formData.selectedWorkOrder = null;
    this.formData.selectedScope = null;
    this.formData.selectedPayType = null;
    this.formData.mealBreak = 'N';
    this.clockinoutService.resetMechanicFormData(this.formData);

    forkJoin([this.fetchLocation$(), this.loadClockInStatus$()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.tryAutoClockAction());
  }

  private fetchLocation$(): Observable<void> {
    return new Observable<void>(observer => {
      if (!this.window.navigator.geolocation) {
        observer.next();
        observer.complete();
        return;
      }
      this.requestingLocation = true;
      this.window.navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.requestingLocation = false;
          this.geolocationEnabled = true;
          this.position = position;
          observer.next();
          observer.complete();
        },
        () => {
          this.requestingLocation = false;
          observer.next();
          observer.complete();
        }
      );
    });
  }

  private loadClockInStatus$(): Observable<void> {
    this.loadingClockStatus = true;
    const currentTime = moment();
    const currentTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
    return this.employeeApiService.getClockInStatus(null, null, false, currentTimeString, '2', null, null).pipe(
      tap(response => {
        if (response != null) {
          this.clockStatus = response.ClockAction;
          this.lastClockinType = response.LastUsedClockinType == null ? 'J' : response.LastUsedClockinType;
          this.loadingClockStatus = false;
          this.clockMoment = moment.utc(response.ClockTime);
          this.lastClockDisplay = this.clockMoment.format('hh:mm A');
          this.mealBreak = response.MealBreak == 'Y';
          this.hadMealBreakToday = response.HadMealBreakToday;
          if (!this.clockStatus && (this.isJobEnabled() || this.isServiceEnabled() || this.isMechanicTypeEnabled())) {
            this.loadJobOrServiceDropDown();
          }
        }
      }),
      map(() => void 0)
    );
  }

  private parseMealBreakSettings(): { prco: number; prgroup: number; minutes: number }[] {
    return (this.portalSettings.ClockInMealBreakMinutes.ConfigValue ?? '')
      .split(';')
      .filter(entry => entry.trim().length > 0)
      .map(entry => {
        const [prco, prgroup, minutes] = entry.split(',').map(Number);
        return { prco, prgroup, minutes };
      })
      .filter(({ prco, prgroup, minutes }) => !isNaN(prco) && !isNaN(prgroup) && !isNaN(minutes) && minutes > 0);
  }

  public getMealBreakMinutes(prco: number, prgroup: number): number | undefined {
    return this.mealBreakSettings.find(s => s.prco === prco && s.prgroup === prgroup)?.minutes;
  }

  showMealBreak(): boolean {
    return this.getMealBreakMinutes(this.employeeInfo.PRCo, this.employeeInfo.PRGroup) != null && !this.hadMealBreakToday;
  }

  postClockAction(ignoreLocation: boolean): void {
    if (!this.loadingClockStatus && !this.requestingLocation) {
      this.performingClockAction = true;
      const requestObj: ClockAction_Request = {
        GPSLat: null,
        GPSLong: null,
        JCJMKeyID: null,
        JCJPKeyID: null,
        SignatureData: null,
        ClockTimeString: null,
        SMTripID: null,
        SMWorkOrderScopeID: null,
        SMPayType: null,
        MealBreak: this.formData.mealBreak == 'Y',
        EMWorkOrderKeyID: null,
        EMWorkOrderItemKeyID: null,
        EquipmentKeyID: null,
        EMCostCodeKeyID: null,
        FromGroupClock: false,
        KioskPass: null,
      };
      if (!ignoreLocation && this.position) {
        requestObj.GPSLat = this.position.coords.latitude;
        requestObj.GPSLong = this.position.coords.longitude;
      }
      if (this.formData.selectedType == 'J' && !this.clockStatus) {
        requestObj.JCJMKeyID = this.formData.selectedJob.JCJMKeyID;
        requestObj.JCJPKeyID = this.formData.selectedPhase.JCJPKeyID;
        if (this.formData.selectedEquipment) requestObj.EquipmentKeyID = this.formData.selectedEquipment.KeyID;
      } else if (this.formData.selectedType == 'S' && !this.clockStatus) {
        requestObj.SMTripID = this.formData.selectedWorkOrder.SMTripID;
        requestObj.SMWorkOrderScopeID = this.formData.selectedScope.SMWorkOrderScopeID;
        requestObj.SMPayType = this.formData.selectedPayType.DatabaseValue;
      } else if (this.formData.selectedType == 'M' && !this.clockStatus) {
        requestObj.EMWorkOrderKeyID = this.formData.selectedEMWorkOrder?.KeyID;
        requestObj.EMWorkOrderItemKeyID = this.formData.selectedEMWorkOrderItem?.KeyID;
        requestObj.EquipmentKeyID = this.formData.selectedEquipment?.KeyID;
        requestObj.EMCostCodeKeyID = this.formData.selectedEMCostCode?.KeyID;
      }
      if (this.signatureData != null) requestObj.SignatureData = this.signatureData;

      const currentTime = moment();
      requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');

      this.employeeApiService
        .postClockAction(requestObj)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(response => {
          if (response != null) {
            this.clockActionPerformed = true;
            this.clockActionSuccess = response.success;
            this.hasWarning = response.warningMessage != null && response.warningMessage != '';
            if (response.success) {
              if (response.resultClockAction) {
                this.message = 'Successfully clocked in at ' + currentTime.format('hh:mm A');
                this.redirectToLogin();
              } else if (response.resultClockAction == false) {
                if (response.warningMessage) {
                  this.alert = {
                    type: 'warning',
                    msg: 'Successfully clocked out at ' + currentTime.format('hh:mm A') + ', ' + response.warningMessage,
                  };
                } else {
                  this.message = 'Successfully clocked out at ' + currentTime.format('hh:mm A');
                  this.redirectToLogin();
                }
              }
            } else {
              this.message = response.error;
            }
          }
        });
    }
  }

  typeChanged(): void {
    if (this.formData.selectedType == 'J') {
      this.resetJobDropdown();
    } else if (this.formData.selectedType == 'S') {
      this.resetServiceDropdown();
      this.getWorkOrder();
    } else if (this.formData.selectedType == 'M') {
      this.clockinoutService.resetMechanicFormData(this.formData);
    }
  }

  private resetJobDropdown(): void {
    this.clockinoutService.resetJobFormData(this.formData);
  }

  private resetServiceDropdown(): void {
    this.formData.selectedWorkOrder = null;
    this.formData.selectedScope = null;
    this.formData.selectedPayType = null;
  }

  private loadJobOrServiceDropDown(): void {
    this.formData.selectedType =
      this.types().length == 1 || !this.types().some(lineType => lineType.Value == this.lastClockinType)
        ? this.types()[0].Value
        : this.lastClockinType;
    if (this.formData.selectedType == 'S') this.getWorkOrder();
  }

  async getWorkOrder(): Promise<void> {
    const response = await this.smService.getWorkOrderTripsByEmployee(this.employeeInfo.Employee, this.employeeInfo.PRCo);
    if (response != null && Array.isArray(response)) {
      this.workOrderList = [];
      this.workOrderList = response.map(val => {
        const scheduledDateDisplay = val.ScheduledDate != null ? moment(val.ScheduledDate).utc().format('DD/MMM') : '';
        return {
          WorkOrder: val.WorkOrder,
          SMTripID: val.SMTripID,
          SMWorkOrderID: val.SMWorkOrderID,
          ScheduledDateDisplay: scheduledDateDisplay,
          Display: (scheduledDateDisplay != '' ? scheduledDateDisplay + ' - ' : '') + 'WO #' + val.WorkOrder + ' ' + val.TripDescription,
          SMCo: val.SMCo,
          WorkOrderDescription: val.WorkOrderDescription,
          TripDescription: val.TripDescription,
        };
      });
    }
  }

  workOrderChanged(): void {
    if (this.formData.selectedWorkOrder != null) {
      this.formData.selectedScope = null;
      this.formData.selectedPayType = null;
      this.getScope();
      this.getPayType();
    }
  }

  async getScope(): Promise<void> {
    this.scopeList = [];
    const response = await this.smService.getWorkOrderScopes('?sMWorkOrderID=' + this.formData.selectedWorkOrder.SMWorkOrderID);
    if (response != null && Array.isArray(response)) {
      this.scopeList = response;
      this.scopeList = response.map(val => ({
        ...val,
        Display: val.Scope + ' - ' + val.ScopeDetails,
      }));
    }
  }

  async getPayType(): Promise<void> {
    this.payTypeList = [];
    const smCo = this.formData.selectedWorkOrder?.SMCo;
    if (smCo != null) {
      const response = await this.smService.getSMPayTypes(smCo);
      if (response != null && Array.isArray(response)) {
        this.payTypeList = response.map(val => ({
          ...val,
          Display: val.DatabaseValue + ' - ' + val.DisplayValue,
        }));
      }
    }
  }

  disableClockInButton(): boolean {
    return (
      this.loadingClockStatus ||
      this.performingClockAction ||
      (this.formData.selectedType == 'J' && !this.formData.isJobFormValid) ||
      (this.formData.selectedType == 'S' &&
        (this.formData.selectedWorkOrder == null || this.formData.selectedScope == null || this.formData.selectedPayType == null)) ||
      (this.formData.selectedType == 'M' && !this.formData.isMechanicformValid)
    );
  }

  canMealBreakClockIn(): boolean {
    const mealBreakThreshold = this.getMealBreakMinutes(this.employeeInfo.PRCo, this.employeeInfo.PRGroup);
    if (mealBreakThreshold != null) {
      const lastClockOut = moment(this.clockMoment.format('YYYY-MM-DD HH:mm:ss'));
      const minutesElapsed = moment().diff(lastClockOut, 'minutes');
      if (minutesElapsed < mealBreakThreshold) return false;
    }
    return true;
  }

  clockOutClicked(): void {
    if (this.requireSign) {
      this.checkSigned();
      if (this.formData.typeSign) {
        this.signatureData = this.rawImgData;
        this.postClockAction(false);
      } else if (!this.signaturePad.isEmpty()) {
        this.signatureData = this.signaturePad.toDataURL().replace(/^data:image\/(png|jpg);base64,/, '');
        this.postClockAction(false);
      }
    }
  }

  clickSign(type: string): void {
    if (this.signaturePad.isEmpty()) {
      this.formData.typeSign = true;
      this.checklistService.getTypeSign(type).then((response: any) => {
        if (response != null) {
          this.rawImgData = response;
          this.submitSignImg = 'data:image/png;base64,' + response;
        }
      });
      this.hideClick = true;
      this.showClickMsg = false;
    } else {
      this.showClickMsg = true;
    }
  }

  clear(): void {
    this.rawImgData = null;
    this.submitSignImg = null;
    this.formData.typeSign = false;
    this.showClickMsg = false;
    this.hideClick = false;
    if (this.signaturePad != null) {
      requestAnimationFrame(() => this.signaturePad.resizeCanvas());
    }
  }

  private redirectToLogin(): void {
    timer(5000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.performLoginRedirect());
  }

  performLoginRedirect(): void {
    let loginURL = '/Account/Login';
    this.utilityService.isLoggedOut = true;
    if (this.kioskLogin) {
      this.window.location.href = '/Account/Logout?kioskLogout=true';
      loginURL = '/Account/Kiosklogin';
    } else {
      this.window.location.href = '/Account/Logout';
    }
    this.window.history.replaceState(null, null, loginURL);
  }

  onJobDropdownChange(data: JobFormData): void {
    this.clockinoutService.updateJobFormData(this.formData, data);
  }

  private checkSigned(): void {
    if (!this.signaturePad.isEmpty()) {
      this.redBorder = false;
      this.disableSubmitSign = true;
    } else {
      this.redBorder = true;
    }
  }

  onMechanicDropdownChange(data: MechanicFormData): void {
    this.clockinoutService.updateMechanicFormData(this.formData, data);
  }

  closeAlert(): void {
    this.alert = null;
  }

  showClockInTypes(): boolean {
    return (
      (this.isJobEnabled() || this.isServiceEnabled() || this.isMechanicTypeEnabled()) &&
      !this.clockStatus &&
      !this.clockActionPerformed &&
      !this.loadingClockStatus
    );
  }

  showClockinfromMealBreak(): boolean {
    return (
      !this.loadingClockStatus &&
      this.mealBreak &&
      !this.canMealBreakClockIn() &&
      !this.clockStatus &&
      !this.isJobEnabled() &&
      !this.isServiceEnabled() &&
      !this.isMechanicTypeEnabled() &&
      !this.clockActionPerformed
    );
  }

  showProcessing(): boolean {
    return !this.isJobEnabled() && !this.isServiceEnabled() && !this.isMechanicTypeEnabled() && this.clockStatus;
  }

  showRegularClockInButtons(): boolean {
    return (
      !this.loadingClockStatus &&
      (!this.mealBreak || this.canMealBreakClockIn()) &&
      ((!this.isJobEnabled() && !this.isServiceEnabled() && !this.isMechanicTypeEnabled()) || this.clockStatus) &&
      !this.clockActionPerformed &&
      (!this.requireSign || (this.requireSign && !this.clockStatus))
    );
  }

  private tryAutoClockAction(): void {
    if (
      this.clockActionPerformed ||
      this.performingClockAction ||
      this.loadingClockStatus ||
      this.requestingLocation ||
      !this.geolocationEnabled ||
      (!this.clockStatus && (this.isJobEnabled() || this.isServiceEnabled() || this.isMechanicTypeEnabled())) ||
      (this.requireSign && this.clockStatus) ||
      (this.clockStatus && this.showMealBreak()) ||
      (this.mealBreak && !this.canMealBreakClockIn())
    ) {
      return;
    }
    this.postClockAction(false);
  }
}
